/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { BuildingOffice2Icon, GlobeEuropeAfricaIcon, ServerIcon, ShareIcon } from "@heroicons/react/24/outline";
import { getIpInfo } from "@/api/requests";
import { useLanguage } from "@/utils/language";

function countryName(code: string, lang: string): string {
  try {
    return new Intl.DisplayNames([lang === "ru" ? "ru" : "en"], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

const Row: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({ icon, label, children }) => (
  <div className="flex min-w-0 items-start gap-2.5">
    <span className="mt-0.5 flex h-4 w-4 flex-none items-center justify-center text-gray-400 dark:text-gray-500 [&_svg]:h-4 [&_svg]:w-4">{icon}</span>
    <div className="min-w-0">
      <div className="text-[10.5px] uppercase tracking-wide text-gray-400 dark:text-gray-500">{label}</div>
      <div className="break-words text-[12.5px] text-gray-900 dark:text-gray-100">{children}</div>
    </div>
  </div>
);

/** Owner, network, country and domain of an external client address. */
export const IpInfoCard: React.FC<{ ip: string }> = ({ ip }) => {
  const { language } = useLanguage();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["ip-info", ip],
    queryFn: () => getIpInfo(ip),
    staleTime: 24 * 3600_000,
    retry: false,
  });

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-white/10 dark:bg-white/[0.04]">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-[12px] font-semibold text-gray-900 dark:text-white">Who owns this address</span>
        <a
          href={`https://stat.ripe.net/app/launchpad/${encodeURIComponent(ip)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11.5px] text-blue-600 hover:underline dark:text-blue-400"
        >
          More details
        </a>
      </div>

      {isLoading ? (
        <div className="grid animate-pulse gap-2 sm:grid-cols-2" aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-8 rounded bg-gray-100 dark:bg-white/10" />
          ))}
        </div>
      ) : isError || !data || (!data.found && !data.host) ? (
        <p className="text-[12px] text-gray-500 dark:text-gray-400">No data about this address</p>
      ) : (
        <div className="grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
          {data.org && (
            <Row icon={<BuildingOffice2Icon />} label="Organisation">
              {data.org}
              {data.asn ? <span className="ml-2 rounded border border-gray-200 px-1.5 py-px font-mono text-[10.5px] text-gray-500 dark:border-white/15 dark:text-gray-400">{`AS${data.asn}`}</span> : null}
            </Row>
          )}
          {data.country && (
            <Row icon={<GlobeEuropeAfricaIcon />} label="Country">
              {countryName(data.country, language)}
              <span className="ml-2 text-gray-400">{data.city ? `${data.city} · ${data.country}` : data.country}</span>
            </Row>
          )}
          {data.prefix && (
            <Row icon={<ShareIcon />} label="Network">
              <span className="font-mono">{data.prefix}</span>
            </Row>
          )}
          {data.host && (
            <Row icon={<ServerIcon />} label="Domain">
              <span className="font-mono">{data.host}</span>
            </Row>
          )}
        </div>
      )}
    </div>
  );
};
